import { InfoBadge } from '@/components/badges/Common';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { equipmentTypeDraftStatusState, equipmentTypeEditState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { EquipmentTypeCandidate, EquipmentTypeId } from '@rock/types';
import { ArrowUpRight } from 'lucide-react';
import type { FC } from 'react';
import { Link } from 'react-router-dom';

export interface PromotionControls {
    busy: boolean;
    /** Set while a Remap all pass is running, e.g. "Remapping 2 of 5…". */
    remapProgress?: string;
    onPromoteNew: () => void;
    onLinkExisting: () => void;
    onUnlink: () => void;
    onRemapAll: () => void;
}

/**
 * Where this candidate's promotion stands, and the one action that moves it forward. The stages —
 * unpromoted → draft type → live type → served — are derived, not stored: `promotionTypeId` says
 * which type the demand is headed for, that type's own draft status says whether it's live yet, and
 * the server stamps `promotedTo` once the last linked custom is remapped onto it.
 */
export const PromotionCard: FC<{ candidate: EquipmentTypeCandidate; unmappedCount: number } & PromotionControls> = ({
    candidate,
    unmappedCount,
    ...controls
}) => {
    if (candidate.promotedTo) {
        return <ServedCard typeId={candidate.promotedTo} />;
    }
    if (candidate.promotionTypeId) {
        return <LinkedTypeCard typeId={candidate.promotionTypeId} unmappedCount={unmappedCount} {...controls} />;
    }
    return <UnpromotedCard {...controls} />;
};

const ServedCard: FC<{ typeId: EquipmentTypeId }> = ({ typeId }) => (
    <Card className="border-green-300 bg-green-50">
        <CardHeader className="pb-4">
            <CardTitle className="text-base">Served by {typeId}</CardTitle>
            <CardDescription>
                Every linked custom has been remapped onto this catalog type — the demand this candidate tracked is met.
            </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
            <TypePageButton typeId={typeId} />
        </CardContent>
    </Card>
);

const UnpromotedCard: FC<PromotionControls> = ({ busy, onPromoteNew, onLinkExisting }) => (
    <Card>
        <CardHeader className="pb-4">
            <CardTitle className="text-base">Not promoted yet</CardTitle>
            <CardDescription>
                Point this demand at a catalog type — seed a new one from the candidate, or link the type the catalog
                already has. Nothing goes live here: the type publishes from its own page, and the linked customs remap
                onto it after that.
            </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2 pt-0">
            <Button size="sm" onClick={onPromoteNew} disabled={busy}>
                Promote to new type…
            </Button>
            <Button size="sm" variant="outline" onClick={onLinkExisting} disabled={busy}>
                Link to existing type…
            </Button>
        </CardContent>
    </Card>
);

/**
 * The candidate points at a type — which may be an unpublished draft, live, or gone (discarded draft,
 * deleted type). Remap is gated on live: `remapEquipmentType` resolves the target from the live
 * catalog and 404s on a draft-only id.
 */
const LinkedTypeCard: FC<{ typeId: EquipmentTypeId; unmappedCount: number } & PromotionControls> = ({
    typeId,
    unmappedCount,
    busy,
    remapProgress,
    onPromoteNew,
    onLinkExisting,
    onUnlink,
    onRemapAll,
}) => {
    const [type, typeLoadable] = useLoadableAtomValue(equipmentTypeEditState(typeId));
    const [draftStatus, draftStatusLoadable] = useLoadableAtomValue(equipmentTypeDraftStatusState(typeId));

    // Both, or neither: a resolved type with a still-loading status would flash "live" at a draft.
    if (typeLoadable.state === 'loading' || draftStatusLoadable.state === 'loading') {
        return (
            <Card>
                <CardHeader className="pb-4">
                    <CardTitle className="text-base">Loading {typeId}...</CardTitle>
                </CardHeader>
            </Card>
        );
    }

    // A failed read says nothing about whether the type still exists, so it must not offer Unlink —
    // dropping the pointer over a transient error would strand the type it names.
    if (typeLoadable.state === 'hasError' || draftStatusLoadable.state === 'hasError') {
        return (
            <Card>
                <CardHeader className="pb-4">
                    <CardTitle className="text-base">Couldn’t load {typeId}</CardTitle>
                    <CardDescription>
                        This candidate is linked to that type, but reading it failed. Reload before acting on it.
                    </CardDescription>
                </CardHeader>
            </Card>
        );
    }

    if (!type) {
        return (
            <Card className="border-amber-300 bg-amber-50">
                <CardHeader className="pb-4">
                    <CardTitle className="text-base">Linked type {typeId} no longer exists</CardTitle>
                    <CardDescription>
                        Its draft was discarded or the type was deleted. Unlink to start the promotion over.
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap items-center gap-2 pt-0">
                    <Button size="sm" variant="outline" onClick={onUnlink} disabled={busy}>
                        Unlink
                    </Button>
                    <Button size="sm" onClick={onPromoteNew} disabled={busy}>
                        Promote to new type…
                    </Button>
                    <Button size="sm" variant="outline" onClick={onLinkExisting} disabled={busy}>
                        Link to existing type…
                    </Button>
                </CardContent>
            </Card>
        );
    }

    const isDraftOnly = draftStatus === 'draft-pending';

    return (
        <Card className={isDraftOnly ? 'border-blue-300 bg-blue-50' : 'border-green-300 bg-green-50'}>
            <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-base">
                    {isDraftOnly ? 'Draft type' : 'Promoted to'} {type.title}
                    <InfoBadge>{typeId}</InfoBadge>
                </CardTitle>
                <CardDescription>
                    {isDraftOnly
                        ? 'Not published yet — the app can’t see it, and customs can’t be remapped onto it. Finish its fields, specs, and guide on the type page, then publish there.'
                        : unmappedCount > 0
                          ? `Live in the catalog. Remap the ${unmappedCount} remaining custom(s) onto it to serve this demand.`
                          : 'Live in the catalog. No linked customs are left to remap.'}
                    {draftStatus === 'draft-differs' && ' It also has unpublished changes waiting on the type page.'}
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center gap-2 pt-0">
                <TypePageButton typeId={typeId} primary />
                {!isDraftOnly && unmappedCount > 0 && (
                    <Button size="sm" variant="outline" onClick={onRemapAll} disabled={busy}>
                        {remapProgress ?? `Remap all ${unmappedCount} onto this type`}
                    </Button>
                )}
                <Button size="sm" variant="ghost" onClick={onUnlink} disabled={busy}>
                    Unlink
                </Button>
            </CardContent>
        </Card>
    );
};

const TypePageButton: FC<{ typeId: EquipmentTypeId; primary?: boolean }> = ({ typeId, primary }) => (
    <Button size="sm" variant={primary ? 'default' : 'outline'} asChild>
        <Link to={`/equipment/types/${encodeURIComponent(typeId)}/details`}>
            Open type page
            <ArrowUpRight className="ml-1 h-4 w-4" />
        </Link>
    </Button>
);
